import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import LoadingSpinner from '../common/LoadingSpinner';

const MetricDetailModal = ({ isOpen, onClose, metricType, stageName = null }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (isOpen && metricType) {
      fetchMetricDetails();
    }
  }, [isOpen, metricType, stageName]);

  const fetchMetricDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `/api/ceo/metric-details/${metricType}`;
      if (metricType === 'stage' && stageName) {
        url += `?stageName=${encodeURIComponent(stageName)}`;
      }

      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching metric details:', error);
      setError(error.response?.data?.error || 'Failed to fetch details');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setData(null);
    setActiveTab('all');
    onClose();
  };

  if (!isOpen) return null;

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': 'bg-gray-100 text-gray-800',
      'Site Visit': 'bg-blue-100 text-blue-800',
      'Proposal Sent': 'bg-yellow-100 text-yellow-800',
      'Agreement Signed': 'bg-green-100 text-green-800',
      'Facility Work': 'bg-purple-100 text-purple-800',
      'Post Reki Approved': 'bg-indigo-100 text-indigo-800',
      'Gym Onboard': 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  const getRoleColor = (role) => {
    const colors = {
      'CEO': 'bg-red-100 text-red-800',
      'COO': 'bg-purple-100 text-purple-800',
      'Head of Acquisition': 'bg-blue-100 text-blue-800',
      'BD Executive': 'bg-green-100 text-green-800',
      'Facility Team': 'bg-yellow-100 text-yellow-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const renderGymList = (gyms, showOverdue = false) => {
    if (!gyms || gyms.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
          </svg>
          <p>No gyms found</p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
        {gyms.map((gym) => (
          <div key={gym.id} className={`p-4 hover:bg-gray-50 ${gym.isOverdue ? 'bg-red-50' : ''}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium text-gray-900">{gym.name}</h4>
                  {gym.isOverdue && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                      Overdue
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{gym.location || 'No location'}</p>
                <div className="mt-2 flex items-center space-x-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStageColor(gym.stage)}`}>
                    {gym.stage}
                  </span>
                  {gym.assignedUser && (
                    <span className="text-xs text-gray-500">
                      Assigned to: {gym.assignedUser.name}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right text-xs text-gray-500">
                {gym.launchDate && (
                  <div>
                    <span className="font-medium">Launch:</span> {format(new Date(gym.launchDate), 'MMM dd, yyyy')}
                  </div>
                )}
                <div>
                  <span className="font-medium">Updated:</span> {format(new Date(gym.updatedAt), 'MMM dd, yyyy')}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderUserList = (users) => {
    if (!users || users.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          <p>No users found</p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
        {users.map((user) => (
          <div key={user.id} className="p-4 hover:bg-gray-50">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium text-gray-900">{user.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                    {user.role}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-600">{user.assignedGymsCount}</div>
                <div className="text-xs text-gray-500">Assigned Gyms</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDeliveryContent = () => {
    if (!data || data.type !== 'delivery') return null;

    const tabs = [
      { id: 'onTime', label: 'On-Time', count: data.summary.onTime, color: 'green' },
      { id: 'late', label: 'Late', count: data.summary.late, color: 'red' },
      { id: 'pending', label: 'Pending', count: data.summary.pending, color: 'yellow' }
    ];

    const getCurrentGyms = () => {
      switch (activeTab) {
        case 'onTime': return data.onTimeGyms;
        case 'late': return data.lateGyms;
        case 'pending': return data.pendingGyms;
        default: return [...data.onTimeGyms, ...data.lateGyms, ...data.pendingGyms];
      }
    };

    return (
      <div>
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`p-4 rounded-lg border-2 transition-colors ${
                activeTab === tab.id
                  ? `border-${tab.color}-500 bg-${tab.color}-50`
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`text-2xl font-bold text-${tab.color}-600`}>{tab.count}</div>
              <div className={`text-sm text-${tab.color}-700`}>{tab.label}</div>
            </button>
          ))}
        </div>

        {/* Gym List */}
        {renderGymList(getCurrentGyms())}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8">
          <div className="text-red-600 mb-2">{error}</div>
          <button
            onClick={fetchMetricDetails}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Try again
          </button>
        </div>
      );
    }

    if (!data) return null;

    if (data.type === 'users') {
      return renderUserList(data.users);
    }

    if (data.type === 'delivery') {
      return renderDeliveryContent();
    }

    return renderGymList(data.gyms);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {loading ? 'Loading...' : data?.title || 'Metric Details'}
            </h2>
            {!loading && data?.description && (
              <p className="text-sm text-gray-500 mt-1">{data.description}</p>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {!loading && data?.count !== undefined && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {data.count} {data.type === 'users' ? 'users' : 'gyms'}
              </span>
            )}
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetricDetailModal;
